import { Component, EventEmitter, Input, Output, ChangeDetectionStrategy, ChangeDetectorRef } from '@angular/core';
import { DateFilterModel, tFilter } from '../../../models/shared/date-filter.model';
import { UserService } from 'app/services';
import { DateFilterName } from 'app/shared/enums';
import { LocalDatePipe } from 'app/shared/pipes/local-date.pipe';

@Component({
    selector: 'app-date-filter',
    templateUrl: './date-filter.component.html',
    styleUrl: './date-filter.component.scss',
    standalone: false,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class DateFilterComponent {
  @Output() readonly onApplyFilter = new EventEmitter<DateFilterModel>();
  @Output() readonly onClear = new EventEmitter<void>();
  @Output() readonly dateFilterChange = new EventEmitter<DateFilterModel>();

  private _dateFilter: DateFilterModel = new DateFilterModel();
  @Input() get dateFilter(): DateFilterModel {
    return this._dateFilter;
  }
  set dateFilter(value: DateFilterModel) {
    if (value === this._dateFilter) return;
    this._dateFilter = value;

    if (value) {
      if (value.startDate && value.endDate) {
        this.selectedDates = [value.startDate, value.endDate];
      }

      this.selected = {
        name: value.name,
        value: value.filterType
      }
    }
  }

  selectedDates: Array<Date> = [];
  selected!: tFilter;
  filterIsOpen = false;
  filterValueIsOpen = false;
  datePicker = false;

  options: Array<tFilter> = [
    {
      name: DateFilterName.Date,
      value: 'today'
    },
    {
      name: DateFilterName.Week,
      value: 'week'
    },
    {
      name: DateFilterName.Month,
      value: 'month'
    },
    {
      name: DateFilterName.Year,
      value: 'year'
    },
    {
      name: DateFilterName.Custom,
      value: 'custom'
    }
  ];

  constructor(public localDatePipe: LocalDatePipe, private userService: UserService, private cdr: ChangeDetectorRef) {
  }

  formatDateForInput(date: Date | undefined): string {
    if (!date) return '';
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  handleStartDateChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.selectedDates[0] = new Date(value + 'T00:00:00');
    }
  }

  handleEndDateChange(event: Event) {
    const value = (event.target as HTMLInputElement).value;
    if (value) {
      this.selectedDates[1] = new Date(value + 'T00:00:00');
    }
  }

  getStartOfWeek(date = new Date()): Date {
    const dayOfWeek = date.getDay(); // 0 (Sun) to 6 (Sat)
    const diff = date.getDate() - dayOfWeek;
    return new Date(date.setDate(diff));
  }

  getEndOfWeek(date = new Date()): Date {
    const dayOfWeek = date.getDay(); // 0 (Sun) to 6 (Sat)
    const diff = 6 - dayOfWeek;
    const end = new Date(date);
    end.setDate(date.getDate() + diff);
    return end;
  }

  handleSetFilter = (filter: tFilter) => {
    let fromDate = new Date();
    let toDate = new Date();

    switch (filter.value) {
      case 'week':
        fromDate = this.getStartOfWeek(fromDate);
        toDate = this.getEndOfWeek(toDate);
      break;
      case 'month':
        fromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
        toDate = new Date(toDate.getFullYear(), toDate.getMonth() + 1, 0);
        break;
      case 'year':
        fromDate = new Date(fromDate.getFullYear(), 0, 1);
        toDate = new Date(toDate.getFullYear(), 11, 31);
        break;
      default:
        ///
        break;
    }

    fromDate.setHours(0, 0, 0, 0);
    this.selectedDates = [fromDate, toDate];
    this.selected = filter;
    this.filterValueIsOpen = false;
    this.cdr.markForCheck();
  };

  applyFilter = () => {
    this.filterIsOpen = !this.filterIsOpen;
    this.cdr.markForCheck();

    if (this.dateFilter.filterType === this.selected.value && this.selected.value !== 'custom') return;
    if (this.selected.value === 'custom'
      && this.dateFilter.startDate?.toLocaleDateString() === this.selectedDates[0].toLocaleDateString()
      && this.dateFilter.endDate?.toLocaleDateString() === this.selectedDates[1].toLocaleDateString()) return;

    this.dateFilter.filterType = this.selected.value;
    this.dateFilter.name = this.selected.name;
    this.dateFilter.startDate = this.selectedDates[0];
    this.dateFilter.endDate = this.selectedDates[1];

    this.userService.saveDashboardFilter(this.dateFilter);
    this.onApplyFilter.emit(this.dateFilter);
  };

  toggleDropdown = () => {
    this.filterValueIsOpen = !this.filterValueIsOpen;
    this.cdr.markForCheck();
  }

  toggleFilterBtn = () => {
    this.filterIsOpen = !this.filterIsOpen;
    this.cdr.markForCheck();
  }

  handleClear = () => {
    this.selectedDates = [];
    let fromDate = new Date();
    fromDate = new Date(fromDate.getFullYear(), fromDate.getMonth(), 1);
    this.dateFilter = new DateFilterModel({
      filterType: 'month',
      name: DateFilterName.Month,
      startDate: fromDate,
      endDate: new Date(),
    });

    this.filterValueIsOpen = false;
    this.userService.saveDashboardFilter(this.dateFilter);
    this.onApplyFilter.emit(this.dateFilter);
    this.cdr.markForCheck();
  };
}
